import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as S from './LogTable.styled';
import { useLogs } from '../hooks';
import type { Log } from '../hooks';
import { convertDate } from 'utils';
import ReactPaginate from 'react-paginate';
import './styles.css';
import MediaQuery from 'react-responsive';
import ArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import ArrowRight from '@mui/icons-material/KeyboardArrowRight';
import More from '@mui/icons-material/MoreHoriz';
import MoreVert from '@mui/icons-material/MoreVert';
import Bin from '@mui/icons-material/DeleteOutline';
import Edit from '@mui/icons-material/Edit';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  ModalParts,
  selectCurrency, selectMetricTime
} from 'pages/redux';
import {
  auditsBoundSelectionHint,
  auditsCheckedToRevealLog,
  auditsTableTypeName,
  auditsTypeAttributeControlVisible,
  auditsTypeAttributeOptionKeys,
  normalizeAuditsTypeAttributeMode,
  ROOM_CHARGE_TYPE,
  type AuditsTypeAttributeMode,
} from '../logUtils';
import AuditsHeaderMenu from './AuditsHeaderMenu';
import AuditDateTimeRangeMenu, { COMBINE_SKU_DATE_ICON_INFO } from './AuditDateTimeRangeMenu';
import { startEndLogIdsFromClicked } from '../auditsLogIdRange';
import { useAuditsLogIdRange } from '../useAuditsLogIdRange';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import CategoryOutlined from '@mui/icons-material/CategoryOutlined';
import QrCode2Outlined from '@mui/icons-material/QrCode2Outlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import NumbersOutlined from '@mui/icons-material/NumbersOutlined';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import {
  selectAuditsTypeAttributeMode,
  selectLogSearch,
  selectLogSearches,
  selectLogSearchOptions,
  selectLogsChecked,
  selectLogsValueMode,
  selectRecentLog,
  selectSingleLogTime,
  selectAssociateElevatedAudits,
  selectAuditsViewAs,
  selectAuditsOnlyLogIds,
  selectAuditsStartLogId,
  selectAuditsEndLogId,
  selectIsComplianceAudit,
  selectComplianceAuditStaffId,
  selectComplianceAuditUsername,
  setAssociateElevatedAudits,
  setAuditsTypeAttributeMode,
  setComplianceAudit,
  setLogsChecked,
  setLogsValueMode,
  setRecentLog,
  setSingleLogTime,
  setAuditsViewAs,
  setAuditsOnlyLogIds,
  setAuditsStartEndLogIds,
  selectAuditsCombineSku,
  selectIsAuditsReportLogsFocus,
  editLog,
} from '../redux';
import DeleteLog from '../modals/DeleteLog';
import RestoreLog from '../modals/RestoreLog';
import CloseReviewedReport from '../modals/CloseReviewedReport';
import PreviewDeletedLog from '../modals/PreviewDeletedLog';
import TimeRangeModal from '../modals/TimeRangeModal';
import Close from '@mui/icons-material/Close';
import SearchOff from '@mui/icons-material/SearchOff';
import ReceiptLong from '@mui/icons-material/ReceiptLong';
import RestoreFromTrash from '@mui/icons-material/RestoreFromTrash';
import HighlightOff from '@mui/icons-material/HighlightOff';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import ModalAction from 'pages/components/Modal/views/ModalAction';
import { toast } from 'utils/toast';
import { selectPermission, selectStaffId, selectUsername } from 'redux/global';
import { useSocketEmit } from 'pages/Navigation/hooks';
import { useSettings } from 'pages/Settings/hooks';
import {
  PERM,
  canModifyAllAudits,
  canUseAuditTools,
  isAssociateManager,
  selectCustomers,
} from 'pages/Settings/redux';
import {
  AUDITS_PER_PAGE,
  auditsTablePageCount,
  paginateAuditsTable,
  sumAuditsTableAmount,
} from '../auditsTableAmount';
import {
  hasActiveLogSearch,
} from '../logSearch';
import {
  buildStaffMaps,
  formatLogActor,
  getLogActorIdentity,
  logMentionsUser,
  resolveActorUser,
} from '../logActor';
import { buildCustomerMaps } from '../logCustomer';
import {
  applyAuditsCombineSku,
  AuditsDisplayLog,
  buildAuditsDisplayLogs,
  buildPermissionByActor,
  combinedSkuDateBounds,
  formatCombinedSkuDateSpan,
  formatCombinedSkuDetailsPreview,
  getLogUnits,
  hasLogUnits,
} from '../auditsDisplayLogs';
import { formatAuditMessageEntry } from '../formatAuditMessageEntry';
import { CombinedSkuDetailsText } from '../CombinedSkuDetailsText';
import { isReportContextStandingLogType } from 'pages/Reporting/reportContextUsers';
import { canCloseFiledReportStandingLog } from 'pages/Reporting/reportRecipientAccess';
import { getCachedIdDocFace } from 'utils/idDocFace';

const GCS_IMAGE_BASE = 'https://storage.googleapis.com/images_wiegfbiwudjdvh/';
type ValueMode = 'value' | 'units';

const resolveImageSrc = (img?: string | null) => {
  if (!img) return '';
  if (img.startsWith('data:') || img.startsWith('http')) return img;
  return `${GCS_IMAGE_BASE}${img}`;
};

const LogTable = (
  {
    checked, triggerAllItems, triggerCashIn, triggerCashOut, setModal, setIsLoading, isLoading
  }
  :
  {
    checked: string[],
    triggerAllItems: boolean,
    triggerCashIn: boolean,
    triggerCashOut: boolean,
    // eslint-disable-next-line no-unused-vars
    setModal: (m: ModalParts | null) => void,
    setIsLoading: Function,
    isLoading: boolean
  }
) => {
  const recentLog = useAppSelector(selectRecentLog);
  const currency = useAppSelector(selectCurrency);
  const logsValueMode = useAppSelector(selectLogsValueMode);
  const auditsTypeAttributeMode = useAppSelector(selectAuditsTypeAttributeMode);
  const [valueMode, setValueMode] = useState<ValueMode>(logsValueMode || 'value');
  const isUnitsMode = valueMode === 'units';
  const metricT = useAppSelector(selectMetricTime);
  const singleLogTime = useAppSelector(selectSingleLogTime);
  const associateElevatedAudits = useAppSelector(selectAssociateElevatedAudits);
  const isComplianceAuditFlag = useAppSelector(selectIsComplianceAudit);
  const complianceAuditStaffId = useAppSelector(selectComplianceAuditStaffId);
  const complianceAuditUsername = useAppSelector(selectComplianceAuditUsername);
  const auditsStartLogId = useAppSelector(selectAuditsStartLogId);
  const auditsEndLogId = useAppSelector(selectAuditsEndLogId);
  // Sessions jump primes singleLogTime / log ids before mount — start filtered to avoid a full-table flash.
  const [isUseMetricTime, setIsUseMetricTime] = useState<boolean>(() => Boolean(
    (singleLogTime?.startDate && singleLogTime?.endDate)
    || auditsStartLogId
    || auditsEndLogId,
  ));
  const [allItems, setAllItems] = useState<boolean>(
    () => !(associateElevatedAudits && logsValueMode === 'units'),
  );
  const [cashIn, setCashIn] = useState<boolean>(true);
  const [cashOut, setCashOut] = useState<boolean>(true);
  const [isShowDeleteLog, setIsShowDeleteLog] = useState<boolean>(false);
  const [isShowEditSave, setIsShowEditSave] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [highlightLog, setHighlightLog] = useState<number>(-1);
  /** Row id to keep highlighted after jumping from an AUD link (cleared after 10s). */
  const [focusLogId, setFocusLogId] = useState<string>('');
  const focusClearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Audit deleted row whose Restore action is toggled open via the list icon. */
  const [restoreOpenId, setRestoreOpenId] = useState<string>('');
  /** User-alert row whose Close report action is toggled open via the same list icon. */
  const [closeReportOpenId, setCloseReportOpenId] = useState<string>('');
  const [editAmount, setEditAmount] = useState<string>('');
  const [editAmountError, setEditAmountError] = useState<string>('');
  const username = useAppSelector(selectUsername);
  const staffId = useAppSelector(selectStaffId);
  const logsChecked = useAppSelector(selectLogsChecked);
  const permission = useAppSelector(selectPermission);
  const auditsViewAs = useAppSelector(selectAuditsViewAs);
  const auditsOnlyLogIds = useAppSelector(selectAuditsOnlyLogIds);
  const auditsCombineSku = useAppSelector(selectAuditsCombineSku);
  const isReportLogsFocus = useAppSelector(selectIsAuditsReportLogsFocus);
  const viewAsIsCurrentUser = Boolean(
    auditsViewAs
    && (
      (staffId && auditsViewAs.staffId && String(auditsViewAs.staffId) === String(staffId))
      || (
        username
        && auditsViewAs.username
        && String(auditsViewAs.username).trim().toLowerCase()
          === String(username).trim().toLowerCase()
      )
    ),
  );
  const useViewAsSender = Boolean(auditsViewAs) && !viewAsIsCurrentUser;
  // Report Show logs: filter as the other party, never above signed-in permission.
  const viewPermission = useViewAsSender
    ? Math.min(Number(permission) || 0, Number(auditsViewAs!.permission) || 0)
    : (Number(permission) || 0);
  const viewStaffId = useViewAsSender ? auditsViewAs!.staffId : staffId;
  const viewUsername = useViewAsSender ? auditsViewAs!.username : username;
  const logSearch = useAppSelector(selectLogSearch);
  const logSearchOptions = useAppSelector(selectLogSearchOptions);
  const logSearches = useAppSelector(selectLogSearches);
  const { logs, updateLocalLog, logGroups, deletedDepartmentNames, deletedInventoryItemNames, deletedInventoryGroupNames } = useLogs();
  const {
    dateById: logIdDateById,
    applyLogIdRangeBound,
    clearLogIdRangeBound,
    clearLogIdRange,
  } = useAuditsLogIdRange(logs);
  const { users } = useSettings();
  const customers = useAppSelector(selectCustomers);
  const headers = logs?.length ? ['type', 'message', 'date', 'value'] : [];
  const { action }: any = useParams();
  const dispatch = useAppDispatch();
  const socketEmit = useSocketEmit();

  const staffMaps = useMemo(() => buildStaffMaps(users), [users]);
  const customerMaps = useMemo(() => buildCustomerMaps(customers), [customers]);

  const userPhotoByName = useMemo(() => {
    const map = new Map<string, string>();
    (users || []).forEach((u) => {
      const src = resolveImageSrc(u.photo);
      if (!src || !u.username) return;
      map.set(u.username.replace(/\s+/g, '').toLowerCase(), src);
      if (u.id != null && String(u.id) !== '') {
        map.set(String(u.id), src);
      }
    });
    return map;
  }, [users]);

  /** Instant face crops only — never kick off MediaPipe from the audits table. */
  const customerPhotoByToken = useMemo(() => {
    const map = new Map<string, string>();
    (customers || []).forEach((c) => {
      // Prefer seeded / stored face photo (images bucket) over in-memory MediaPipe cache.
      const seeded = resolveImageSrc(c.facePhoto);
      const src = seeded || getCachedIdDocFace(c.idDocUrl);
      if (!src) return;
      if (c.id != null && String(c.id) !== '') {
        map.set(String(c.id), src);
      }
      if (c.name) {
        map.set(c.name.replace(/\s+/g, '').toLowerCase(), src);
      }
    });
    return map;
  }, [customers]);

  const auditsMax = AUDITS_PER_PAGE;

  const { startDate, endDate } = (singleLogTime || metricT || {});

  const permissionByActor = useMemo(() => buildPermissionByActor(users || []), [users]);

  /** Compliance review unlock: Associate Managers may see higher-role audits until range changes/off. */
  const complianceReviewActive = Boolean(
    associateElevatedAudits
    && isUseMetricTime
    && startDate
    && endDate,
  );
  /**
   * Compliance → associated audits: after the audited cashier's first
   * department units sale, hide other users. Off when time range is toggled
   * off or changed.
   */
  const isComplianceAudit = Boolean(
    isComplianceAuditFlag
    && isUseMetricTime
    && startDate
    && endDate
    && complianceAuditStaffId,
  );
  const complianceReviewAllActors = Boolean(
    isAssociateManager(permission) && complianceReviewActive,
  );

  const sessionActor = useMemo(() => ({ staffId, username }), [staffId, username]);

  const actorPermission = (log: {
    message: string;
    created_by?: string | null;
  }) => {
    const actor = getLogActorIdentity(log);
    if (!actor) return null;
    const resolved = resolveActorUser(actor, staffMaps);
    return permissionByActor.get(String(actor))
      ?? (resolved?.id != null ? permissionByActor.get(String(resolved.id)) : undefined)
      ?? (resolved?.username
        ? permissionByActor.get(resolved.username.replace(/\s+/g, '').toLowerCase())
        : undefined)
      ?? null;
  };

  /**
   * Compliance → associated audits: bare higher-role audits (no data.units) stay hidden
   * for every viewer, including Manager+ / the author of the log.
   */
  const isComplianceBlockedBareHighPermLog = (log: {
    message: string;
    created_by?: string | null;
    units?: string | number | null;
    data?: { units?: string | number | null };
  }) => {
    if (!complianceReviewActive) return false;
    if (hasLogUnits(log)) return false;
    const actorPerm = actorPermission(log);
    if (actorPerm != null && actorPerm <= PERM.SUB_MANAGER) return false;
    return true;
  };

  /** Peer-level modify gate — unrestricted view types stay non-editable above peer. */
  const canAssociateModifyLog = (log: {
    type?: string;
    message: string;
    created_by?: string | null;
    units?: string | number | null;
    data?: { units?: string | number | null };
  }) => {
    if (isComplianceBlockedBareHighPermLog(log)) return false;
    const actor = getLogActorIdentity(log);
    if (!actor) return false;
    if (logMentionsUser(log.message, sessionActor)
      || (staffId && String(log.created_by || '') === String(staffId))) {
      return true;
    }
    const actorPerm = actorPermission(log);
    if (actorPerm == null) return false;
    if (actorPerm <= PERM.SUB_MANAGER) return true;
    return Boolean(complianceReviewAllActors && hasLogUnits(log));
  };

  const hasActiveSearch = hasActiveLogSearch(logSearches, logSearch);

  const searchArchives = useMemo(() => ({
    deletedTypes: deletedInventoryItemNames,
    deletedGroups: deletedInventoryGroupNames,
    deletedDepartments: deletedDepartmentNames,
  }), [deletedInventoryItemNames, deletedInventoryGroupNames, deletedDepartmentNames]);

  const departmentNames = useMemo(
    () => new Set(logGroups.Departments || []),
    [logGroups.Departments],
  );

  const sharedDisplayParams = {
    logs: logs || [],
    permission: viewPermission,
    staffId: viewStaffId,
    username: viewUsername,
    users: users || [],
    logSearches: logSearches || [],
    logSearch: logSearch || '',
    logSearchOptions,
    staffMaps,
    customerMaps,
    departmentNames,
    searchArchives,
    logGroups: logGroups as Record<string, string[]>,
    startDate,
    endDate,
    valueMode: (isUnitsMode ? 'units' : 'value') as 'value' | 'units',
    associateElevatedAudits: Boolean(associateElevatedAudits),
    isComplianceAudit,
    complianceAuditStaffId,
    onlyLogIds: auditsOnlyLogIds,
    startLogId: auditsStartLogId,
    endLogId: auditsEndLogId,
  };

  // Permission + search + sort + type checks (no time/cash) — recent-log jump helper.
  const getSelectLogs = (checked1: string[]) => buildAuditsDisplayLogs({
    ...sharedDisplayParams,
    checked: checked1,
    useTimeRange: false,
    allItems: true,
    cashIn: true,
    cashOut: true,
    associateElevatedAudits: false,
    isComplianceAudit: false,
  });

  // Same pipeline as Reporting (useAuditsDisplayLogs → buildAuditsDisplayLogs).
  const rawDisplayLogs = buildAuditsDisplayLogs({
    ...sharedDisplayParams,
    checked,
    useTimeRange: isUseMetricTime,
    allItems,
    cashIn,
    cashOut,
  });
  const allDisplayLogs: AuditsDisplayLog[] = useMemo(() => {
    if (!auditsCombineSku) return rawDisplayLogs;
    return applyAuditsCombineSku(
      rawDisplayLogs,
      (log) => auditsTableTypeName(
        log,
        checked,
        logGroups,
        auditsTypeAttributeMode,
      ),
      isUnitsMode ? 'units' : 'value',
    );
  }, [
    rawDisplayLogs,
    auditsCombineSku,
    isUnitsMode,
    checked,
    logGroups,
    auditsTypeAttributeMode,
  ]);
  const displayLogs = paginateAuditsTable(allDisplayLogs, page, auditsMax);
  const pagesCount = auditsTablePageCount(allDisplayLogs.length, auditsMax);

  useEffect(() => {
    setPage(1);
  }, [auditsCombineSku]);

  useEffect(() => {
    if (valueMode === logsValueMode) return;
    setValueMode(logsValueMode);
    // Compliance units jump: hide N/A rows by default (Non-Unit off).
    setAllItems(!(associateElevatedAudits && logsValueMode === 'units'));
    if (logsValueMode === 'value') {
      setCashIn(true);
      setCashOut(true);
    }
    setPage(1);
  }, [logsValueMode, valueMode, associateElevatedAudits]);

  // Compliance → view sales units audits: start with Non-Unit off so bare directs stay hidden.
  useEffect(() => {
    if (associateElevatedAudits && logsValueMode === 'units') {
      setAllItems(false);
    }
  }, [associateElevatedAudits, logsValueMode]);

  useEffect(() => {
    if (recentLog) {
      setIsUseMetricTime(false);
      setAllItems(true);
      setCashIn(true);
      setCashOut(true);
      const thisLog = (logs || []).find(
        (l) => l.id.toString() === recentLog.toString()
      );
      if (thisLog) {
        const newChecked = auditsCheckedToRevealLog(checked, thisLog);
        const thisIndex = getSelectLogs(newChecked).findIndex((l) => l.id.toString() === recentLog
          .toString());
        if (thisIndex > -1) {
          const thisPage = Math.ceil(Number(thisIndex) / auditsMax);
          dispatch(setLogsChecked(newChecked));
          setPage(thisPage < 1 ? 1 : thisPage);
        }
      }
    }
  }, [recentLog, logs]);

  useEffect(() => {
    setIsUseMetricTime(false);
    setAllItems(true);
    setCashIn(true);
    setCashOut(true);
  }, [triggerAllItems]);

  useEffect(() => {
    setIsUseMetricTime(false);
    setCashIn(true);
  }, [triggerCashIn]);

  useEffect(() => {
    setIsUseMetricTime(false);
    setCashOut(true);
  }, [triggerCashOut]);

  useEffect(() => {
    dispatch(setRecentLog(''));
    if (!action) return undefined;
    const action1 = action.replace('svg1ere', '').replace('xxyv', '');
    setIsUseMetricTime(true);
    if (Number(action1) === 0) {
      return undefined;
    }
    setAllItems(false);
    if (Number(action1) === 1) {
      setCashOut(false);
    }
    if (Number(action1) === 2) {
      setCashIn(false);
    }
    if (Number(action1) === 4) {
      setAllItems(true);
    }
    return undefined;
  }, []);

  // Keep range toggle on when Sessions (or other jumps) re-prime singleLogTime or log ids.
  useEffect(() => {
    if (singleLogTime?.startDate && singleLogTime?.endDate) {
      setIsUseMetricTime(true);
      return;
    }
    if (!auditsStartLogId && !auditsEndLogId) {
      setIsUseMetricTime(false);
    }
  }, [singleLogTime, auditsStartLogId, auditsEndLogId]);

  useEffect(() => {
    if (auditsStartLogId || auditsEndLogId) {
      setIsUseMetricTime(true);
    }
  }, [auditsStartLogId, auditsEndLogId]);

  const logIdRangeActive = Boolean(auditsStartLogId || auditsEndLogId);
  const timeRangeControlOn = isUseMetricTime || logIdRangeActive;

  const openTimeRangeModal = () => {
    setModal({
      component: (
        <TimeRangeModal
          isOn={timeRangeControlOn}
          onApply={({ isOn }) => {
            setIsUseMetricTime(isOn);
            if (!isOn) {
              dispatch(setSingleLogTime(null));
              dispatch(setAuditsStartEndLogIds(null));
              dispatch(setAssociateElevatedAudits(false));
              dispatch(setComplianceAudit({ active: false }));
              dispatch(setAuditsViewAs(null));
            }
          }}
          onExit={() => setModal(null)}
        />
      ),
      exitOnBgClick: () => setModal(null),
    });
  };

  const clearAuditsTimeRange = () => {
    dispatch(setSingleLogTime(null));
    clearLogIdRange();
    dispatch(setAssociateElevatedAudits(false));
    dispatch(setComplianceAudit({ active: false }));
    dispatch(setAuditsViewAs(null));
    setIsUseMetricTime(false);
    setPage(1);
  };

  const sourceIdsForLogRow = (log: { id?: string, combinedSourceIds?: string[] }) => (
    log.combinedSourceIds?.length
      ? log.combinedSourceIds.map((id) => String(id))
      : [String(log.id || '')]
  );

  const logIdBoundsForRow = (log: { id?: string, combinedSourceIds?: string[] }) => {
    const sourceIds = sourceIdsForLogRow(log);
    const bounds = startEndLogIdsFromClicked(sourceIds, logIdDateById);
    return {
      sourceIds,
      startLogId: bounds.startLogId || String(log.id || ''),
      endLogId: bounds.endLogId || bounds.startLogId || String(log.id || ''),
    };
  };

  /** Row → start/end log-id slice (same ranking as /reportings). */
  const applyLogIdToTimeRange = (which: 'start' | 'end', sourceIds: string[]) => {
    applyLogIdRangeBound(which, sourceIds);
    dispatch(setAssociateElevatedAudits(false));
    dispatch(setComplianceAudit({ active: false }));
    setIsUseMetricTime(true);
    setPage(1);
  };

  /** Unset a bound pinned to this row. Remaining bound stays; none left turns the filter off. */
  const clearLogIdFromTimeRange = (which: 'start' | 'end') => {
    const prevStart = String(auditsStartLogId || '').trim();
    const prevEnd = String(auditsEndLogId || '').trim();
    if (!prevStart && !prevEnd) {
      clearAuditsTimeRange();
      return;
    }
    if (!prevStart || !prevEnd || prevStart === prevEnd) {
      clearAuditsTimeRange();
      return;
    }
    clearLogIdRangeBound(which);
    dispatch(setAssociateElevatedAudits(false));
    dispatch(setComplianceAudit({ active: false }));
    setIsUseMetricTime(true);
    setPage(1);
  };

  const exitReportLogsFocus = () => {
    setIsUseMetricTime(false);
    dispatch(setSingleLogTime(null));
    dispatch(setAuditsViewAs(null));
    dispatch(setAuditsOnlyLogIds(null));
    dispatch(setAuditsStartEndLogIds(null));
  };

  const FormatLogs = () => (
    <>
      {isReportLogsFocus ? (
        <S.ReportViewAsBanner>
          <span>
            {auditsViewAs && !viewAsIsCurrentUser ? (
              <>
                Viewing Audits as
                {' '}
                {auditsViewAs.label || auditsViewAs.username || 'the other party'}
                {' '}
                (same logs as the staff report). Click exit to view as me.
              </>
            ) : auditsOnlyLogIds?.length === 1
              ? 'Showing this log from the report. Click exit to view as me.'
              : auditsOnlyLogIds?.length
                ? 'Showing these logs from the report. Click exit to view as me.'
                : 'Viewing Audits as yourself (report filters still apply).'}
          </span>
          <S.ReportViewAsActions>
            <S.ReportViewAsExit type="button" onClick={exitReportLogsFocus}>
              Exit
            </S.ReportViewAsExit>
          </S.ReportViewAsActions>
        </S.ReportViewAsBanner>
      ) : null}
      <S.FormatLogs $hidden={isReportLogsFocus} aria-hidden={isReportLogsFocus}>
      <S.TimeRangeSet
        type="button"
        $on={timeRangeControlOn && !auditsCombineSku}
        disabled={auditsCombineSku}
        title={auditsCombineSku ? COMBINE_SKU_DATE_ICON_INFO : undefined}
        onClick={() => {
          if (auditsCombineSku) return;
          openTimeRangeModal();
        }}
      >
        <S.All>
          Time
          <MediaQuery minWidth={Number(S.logSmallRes2.replace('px', ''))}>
            {' '}
            range
          </MediaQuery>
        </S.All>
      </S.TimeRangeSet>
      {!isUnitsMode && (
        <>
          <S.FormatSet
            onClick={() => {
              if (allItems && displayLogs.length === 0) return;
              setAllItems(false);
              setCashIn(!cashIn);
              if (cashIn) {
                setCashOut(true);
              }
            }}
          >
            <S.ShowCashIn>
              <MediaQuery minWidth={311}>
                Cash-
              </MediaQuery>
              In
            </S.ShowCashIn>
            <S.Select
              type="checkbox"
              checked={cashIn}
              disabled={Boolean(allItems && displayLogs.length === 0)}
              readOnly
            />
          </S.FormatSet>
          <S.FormatSet
            onClick={() => {
              if ((allItems && displayLogs.length === 0)) return;
              setAllItems(false);
              setCashOut(!cashOut);
              if (cashOut) {
                setCashIn(true);
              }
            }}
          >
            <S.ShowCashOut>
              <MediaQuery minWidth={Number(S.logMiniRes.replace('px', ''))}>
                Cash-
              </MediaQuery>
              Out
            </S.ShowCashOut>
            <S.Select
              type="checkbox"
              checked={cashOut}
              disabled={Boolean(allItems && displayLogs.length === 0)}
              readOnly
            />
          </S.FormatSet>
        </>
      )}
      <S.FormatSet
        onClick={() => {
          if (allItems && displayLogs.length === 0) {
            return;
          }
          setAllItems(!allItems);
          if (!allItems && !isUnitsMode) {
            setCashIn(true);
            setCashOut(true);
          }
        }}
      >
        <S.All>
          <MediaQuery minWidth={Number(S.logSmallRes.replace('px', ''))}>
            {isUnitsMode ? 'Non-Unit' : 'Non-Cash'}
          </MediaQuery>
          <MediaQuery maxWidth={Number(S.logSmallRes.replace('px', '')) - 1}>
            All
          </MediaQuery>
        </S.All>
        <S.Select
          type="checkbox"
          checked={allItems}
          disabled={Boolean(allItems && displayLogs.length === 0)}
          readOnly
        />
      </S.FormatSet>
    </S.FormatLogs>
    </>
  );

  const cashGroupTypes = [
    ...logGroups.Hospitality,
    ...logGroups.Departments,
    ...(logGroups['Department entries'] || []),
    ...logGroups.Inventory,
    ...logGroups['Inventory Group'],
    ROOM_CHARGE_TYPE,
  ];
  const isCashGroup = Boolean(
    cashGroupTypes.filter((g) => !checked.includes(g)).length !== cashGroupTypes.length
  );
  const boundSelectionHint = auditsBoundSelectionHint(checked, logGroups);

  const canModifyLogEntry = (log: { type?: string; message: string; created_by?: string | null }) => {
    if (canModifyAllAudits(permission)) return true;
    if (isAssociateManager(permission)) return canAssociateModifyLog(log);
    return false;
  };

  /** Jump to an audit by id: page + center + highlight for 10s. */
  const jumpToAuditId = (targetId: string) => {
    const id = String(targetId);
    const exists = (logs || []).some((l) => l.id.toString() === id);
    if (!exists) {
      toast('Audit entry not found', { type: 'error' });
      return;
    }
    setFocusLogId(id);
    if (focusClearTimer.current) clearTimeout(focusClearTimer.current);
    focusClearTimer.current = setTimeout(() => setFocusLogId(''), 10000);
    dispatch(setRecentLog(id));
  };

  const formattedEntry = (
    entry: string,
    opts?: { unitsMode?: boolean, parentLog?: Log },
  ) => formatAuditMessageEntry(entry, {
    currency,
    staffMaps,
    customerMaps,
    userPhotoByName,
    customerPhotoByToken,
    unitsMode: opts?.unitsMode,
    parentLog: opts?.parentLog,
    canPreviewDeletedAudit: Boolean(
      opts?.parentLog
      && canModifyLogEntry(opts.parentLog)
      && opts.parentLog.type === 'Audit deleted'
      && opts.parentLog.logTable?.id,
    ),
    onAuditIdClick: (targetId) => jumpToAuditId(targetId),
    onPreviewDeletedAudit: (targetId) => {
      const parentLog = opts?.parentLog;
      if (!parentLog?.logTable) return;
      setModal({
        component: (
          <PreviewDeletedLog
            onExit={() => setModal(null)}
            snapshot={parentLog.logTable as Log}
            auditId={targetId}
          />
        ),
        exitOnBgClick: () => setModal(null),
      });
    },
  });

  // Footer total for any role when cash/amount columns are in view.
  const isShowTotal = isCashGroup;

  const sumLogAmount = (items: {
    message: string;
    value?: string | number;
    units?: string | number | null;
    data?: { units?: string | number | null };
    created_by?: string | null;
  }[]) => {
    const amountLogs = (isAssociateManager(permission) && !complianceReviewAllActors)
      ? items.filter((l) => (
        logMentionsUser(l.message, sessionActor)
        || Boolean(staffId && String(l.created_by || '') === String(staffId))
      ))
      : items;
    return sumAuditsTableAmount(amountLogs, isUnitsMode ? 'units' : 'value');
  };

  // All filtered rows — page-only sums jump (0 on page 1, 16 on page 10) when
  // early pages are N/A hospitality / later pages hold a handful of cash rows.
  const total = sumLogAmount(allDisplayLogs);

  const openOrCloseValueMore = (ind: number, open?: boolean) => {
    const moreIconsCont = document.getElementById(`moreIconsCont${ind}`);
    const editAmountCont = document.getElementById(`editAmountCont${ind}`);
    const valueTD = document.getElementById(`valueTD${ind}`);
    if (moreIconsCont && valueTD && editAmountCont) {
      if ((moreIconsCont.style.height === '0px' && editAmountCont.style.height === '0px') && open) {
        valueTD.style.zIndex = '2';
        moreIconsCont.style.pointerEvents = 'auto';
        moreIconsCont.style.height = '92px';
        moreIconsCont.style.opacity = '1';
      } else {
        valueTD.style.zIndex = '1';
        moreIconsCont.style.pointerEvents = 'none';
        moreIconsCont.style.height = '0px';
        moreIconsCont.style.opacity = '0';
        editAmountCont.style.pointerEvents = 'none';
        editAmountCont.style.height = '0px';
        editAmountCont.style.opacity = '0';
      }
    }
  };

  const onClickValueMore = (ind: number) => {
    const moreIconsCont = document.getElementById(`moreIconsCont${ind}`);
    const editAmountCont = document.getElementById(`editAmountCont${ind}`);
    if (moreIconsCont && editAmountCont && (moreIconsCont.style.height !== '0px' ||
      editAmountCont.style.height !== '0px')) {
      setHighlightLog(-1);
    } else {
      setHighlightLog(ind);
    }
    for (let i = 0; i <= displayLogs.length; i += 1) {
      openOrCloseValueMore(i, ind === i);
    }
  };

  const onExitValueTD = () => {
    if (!isShowDeleteLog && !isLoading && !isShowEditSave) onClickValueMore(-1);
  };

  const onClickEdit = (ind: number) => {
    openOrCloseValueMore(ind);
    const editAmountCont = document.getElementById(`editAmountCont${ind}`);
    const valueTD = document.getElementById(`valueTD${ind}`);
    if (editAmountCont && valueTD) {
      valueTD.style.zIndex = '2';
      editAmountCont.style.pointerEvents = 'auto';
      editAmountCont.style.height = '176px';
      editAmountCont.style.opacity = '1';
    }
  };

  const onChangeEditing = (value: string) => {
    setEditAmountError('');
    const formatted = value.replaceAll(',', '').replaceAll(currency, '');
    if (formatted[0] === '-') {
      if (
        (formatted.length >= 2 && formatted.slice(1, formatted.length - 1).includes('-')) ||
        (/\D+/.test(formatted.slice(1, formatted.length)))
      ) {
        setEditAmountError('Not a valid entry');
        return;
      }
    } else {
      if (formatted.includes('-') || /\D+/.test(formatted)) {
        setEditAmountError('Not a valid entry');
        return;
      }
    }
    setEditAmount(formatted);
  };

  const showEditSave = () => {
    setIsShowEditSave(true);
    setModal({
      component: (
        <ModalAction
          header="Save amount edit"
          item="This audit entry"
          info={`Change the value of ${currency}${Number(displayLogs[highlightLog].value).toLocaleString()} to 
            ${currency}${Number(editAmount).toLocaleString()}?`}
          onExit={() => {
            setIsShowEditSave(false);
            setModal(null);
          }}
          onAct={() => onSubmitEdit()}
        />
      ),
      exitOnBgClick: () => {
        setIsShowEditSave(false);
        setModal(null);
      }
    });
  };

  const onClickSaveEdit = () => {
    setEditAmountError('');
    if (!editAmount) {
      setEditAmountError('Amount is needed');
      return;
    }
    if (editAmount === displayLogs[highlightLog].value) {
      setEditAmountError('No changes made');
      return;
    }
    showEditSave();
  };

  const onSubmitEdit = async () => {
    setIsShowEditSave(false);
    setModal(null);
    setIsLoading(true);

    const logToEdit = {
      editId: displayLogs[highlightLog].id,
      addLogMessage: `&${displayLogs[highlightLog].type}& &L&${displayLogs[highlightLog].id}&L& amount changed from &P&${displayLogs[highlightLog].value}&P& to &P&${editAmount}&P& by ${formatLogActor(staffId, username)}`,
      value: editAmount,
    };

    const res = await dispatch(editLog(logToEdit));

    if (res.status === 'success') {
      const newLogsChecked = [...logsChecked.filter((c) => c !== 'Audit change'), 'Audit change'];
      dispatch(setLogsChecked(newLogsChecked));
      const newLog = { ...displayLogs[highlightLog], value: editAmount };
      updateLocalLog(newLog);
      socketEmit('update_log', { log: newLog });

      // This waits for DB from editLog() to show it's addLog changes
      await new Promise((resolve) => { setTimeout(resolve, 400); });

      setIsLoading(false);
      setPage(1);

      const editAmountCont = document.getElementById(`editAmountCont${highlightLog}`);
      const valueTD = document.getElementById(`valueTD${highlightLog}`);
      if (editAmountCont && valueTD) {
        valueTD.style.zIndex = '1';
        editAmountCont.style.pointerEvents = 'none';
        editAmountCont.style.height = '0px';
        editAmountCont.style.opacity = '0';
      }

      setHighlightLog(-1);
      toast('Successfully edited audit entry', { type: 'success' });
      return;
    }

    setIsLoading(false);
    showEditSave();
    toast(res.data, { type: 'error' });
  };

  let recentLogTimer: any;

  useEffect(() => {
    if (recentLog) {
      clearTimeout(recentLogTimer);
      const thisIndex = displayLogs.findIndex((l) => l.id.toString() === recentLog.toString());
      document.getElementById(`tdIndex${thisIndex}`)?.scrollIntoView(
        { behavior: 'smooth', block: 'center', inline: 'center' }
      );
      recentLogTimer = setTimeout(() => dispatch(setRecentLog('')), 400);
    } else {
      document.getElementById('table_cont')?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [page, cashIn, cashOut, allItems, checked, valueMode]);

  useEffect(() => {
    if (!recentLog) {
      setPage(1);
    }
  }, [cashIn, cashOut, allItems, checked, valueMode]);

  useEffect(() => {
    if (cashGroupTypes.filter((b) => checked.includes(b)).length === 0 && checked.length) {
      setAllItems(true);
      setCashIn(true);
      setCashOut(true);
    }
  }, [checked]);

  useEffect(() => {
    setEditAmount(displayLogs[highlightLog]?.value || '');
    setEditAmountError('');
  }, [highlightLog]);

  const focusInd = focusLogId
    ? displayLogs.findIndex((l) => l.id.toString() === focusLogId)
    : -1;
  const rowHighlight = focusInd >= 0 ? focusInd : highlightLog;

  // Keep the jumped-to audit centered while the 10s focus highlight is active.
  useEffect(() => {
    if (!focusLogId || focusInd < 0) return undefined;
    const t = window.setTimeout(() => {
      document.getElementById(`tdIndex${focusInd}`)?.scrollIntoView({
        behavior: 'smooth', block: 'center', inline: 'center',
      });
    }, 50);
    return () => window.clearTimeout(t);
  }, [focusLogId, focusInd, page]);

  useEffect(() => () => {
    if (focusClearTimer.current) clearTimeout(focusClearTimer.current);
  }, []);

  return (
    <S.Container isCashGroup={isShowTotal}>
      <S.AllCont>
        <S.Cont1 id="table_cont">
          <S.Table
            isCashGroup={isCashGroup}
            itemCount={(auditsMax * page).toString().length}
          >
            <S.THead>
              <S.TR>
                {
                  headers.length ? (
                    <S.TH7
                      isEmpty={displayLogs.length === 0}
                      itemCount={(auditsMax * page).toString().length}
                      isCashGroup={isCashGroup}
                    >
                      S/no
                    </S.TH7>
                  ) : (null)
                }
                {
                  headers.map((hTmp: string) => {
                    let h = JSON.parse(JSON.stringify([...hTmp]));
                    h[0] = h[0].toUpperCase();
                    h = h.join('');
                    if (h === 'Message') {
                      h = 'Details';
                      return (
                        <S.TH2
                          isEmpty={displayLogs.length === 0}
                          key={`logH_${h}`}
                          isCashGroup={isCashGroup}
                        >
                          {h}
                        </S.TH2>
                      );
                    }
                    if (h === 'Value') {
                      if (!isCashGroup) {
                        return null;
                      }
                      return (
                        <S.TH4
                          isEmpty={displayLogs.length === 0}
                          key={`logH_${h}`}
                          isCashGroup={isCashGroup}
                        >
                          <AuditsHeaderMenu
                            ariaLabel="Attribute value column"
                            title="Attribute by value or units"
                            sectionLabel="Attribute by"
                            value={valueMode}
                            lineTrigger
                            flush
                            options={[
                              {
                                value: 'value',
                                label: 'Value',
                                hint: 'Show monetary amounts',
                                icon: <PaymentsOutlined fontSize="inherit" />,
                              },
                              {
                                value: 'units',
                                label: 'Units',
                                hint: 'Show inventory unit counts',
                                icon: <NumbersOutlined fontSize="inherit" />,
                              },
                            ]}
                            onChange={(next) => {
                              const mode = (next === 'units' ? 'units' : 'value') as ValueMode;
                              setValueMode(mode);
                              dispatch(setLogsValueMode(mode));
                              setAllItems(true);
                              if (mode === 'value') {
                                setCashIn(true);
                                setCashOut(true);
                              }
                              setPage(1);
                            }}
                          />
                        </S.TH4>
                      );
                    }
                    if (h === 'Type') {
                      h = 'Audit';
                      const showAttribute = auditsTypeAttributeControlVisible(
                        checked,
                        logGroups,
                      );
                      const attributeKeys = auditsTypeAttributeOptionKeys(
                        checked,
                        logGroups,
                      );
                      const effectiveAttributeMode = normalizeAuditsTypeAttributeMode(
                        auditsTypeAttributeMode,
                        checked,
                        logGroups,
                      );
                      const attributeOptions = attributeKeys.map((key) => {
                        if (key === 'department') {
                          return {
                            value: 'department',
                            label: 'Department',
                            hint: 'Group rows by department',
                            icon: <AccountTreeOutlined fontSize="inherit" />,
                          };
                        }
                        if (key === 'inventory_group') {
                          return {
                            value: 'inventory_group',
                            label: 'Inventory group',
                            hint: 'Group rows by inventory group',
                            icon: <CategoryOutlined fontSize="inherit" />,
                          };
                        }
                        return {
                          value: 'sku',
                          label: 'SKU',
                          hint: 'Show the actual inventory type on each log',
                          icon: <QrCode2Outlined fontSize="inherit" />,
                        };
                      });
                      return (
                        <S.TH3
                          isEmpty={displayLogs.length === 0}
                          key={`logH_${h}`}
                          isCashGroup={isCashGroup}
                        >
                          <S.AuditHeaderRow>
                            {h}
                            {showAttribute ? (
                              <AuditsHeaderMenu
                                ariaLabel="Attribute audit types"
                                title="Attribute temporary types"
                                sectionLabel="Attribute by"
                                value={effectiveAttributeMode}
                                lineTrigger
                                trigger={<LayersOutlined fontSize="inherit" />}
                                options={attributeOptions}
                                onChange={(next) => {
                                  dispatch(setAuditsTypeAttributeMode(
                                    next as AuditsTypeAttributeMode,
                                  ));
                                }}
                              />
                            ) : null}
                          </S.AuditHeaderRow>
                        </S.TH3>
                      );
                    }
                    return (
                      <S.TH
                        isEmpty={displayLogs.length === 0}
                        key={`logH_${h}`}
                        isCashGroup={isCashGroup}
                      >
                        {h}
                      </S.TH>
                    );
                  })
                }
              </S.TR>
            </S.THead>
            <tbody style={{ border: 'none', zIndex: '1' }}>
              {
                displayLogs?.map((log, i) => {
                  const isLast = i === displayLogs.length - 1;
                  const isNew = new Date().getTime() - new Date(log.updatedAsOf).getTime() <=
                    (2 * 60 * 1000);
                  const isCombinedSku = log.isCombinedSku === true;
                  const combinedTooltip = isCombinedSku
                    ? `Combined log — ${log.combinedLogCount || 0} value entries rolled up by SKU`
                    : undefined;

                  return (
                    <S.TR2
                      key={`logTR_${isCombinedSku ? `combined_${log.combinedSourceIds?.join('_')}` : log.type}_${i}`}
                      title={combinedTooltip}
                    >
                      <S.TD3
                        isLast={isLast}
                        ind={i}
                        highlighted={rowHighlight}
                        isNew={isNew}
                        id={`tdIndex${i}`}
                      >
                        {i + ((page - 1) * auditsMax) + 1}
                      </S.TD3>
                      {
                        headers.map((h) => {
                          let entry: any = (log as any)[h];
                          if (h === 'type') {
                            entry = auditsTableTypeName(
                              log,
                              checked,
                              logGroups,
                              auditsTypeAttributeMode,
                            );
                          }
                          if (h === 'date') {
                            if (isCombinedSku) {
                              const bounds = combinedSkuDateBounds(log.sourceDates || []);
                              const dateLabel = formatCombinedSkuDateSpan(log.sourceDates || []);
                              const rangeTitle = bounds
                                ? `${convertDate(bounds.start).replace('. 00:00am', '').replace('00:00am', '').trim()} – ${convertDate(bounds.end).replace('. 00:00am', '').replace('00:00am', '').trim()}`
                                : combinedTooltip;
                              const idBounds = logIdBoundsForRow(log);
                              return (
                                <S.TD
                                  key={`LogTD_${h}_${i}`}
                                  isLast={isLast}
                                  ind={i}
                                  highlighted={rowHighlight}
                                  isNew={isNew}
                                >
                                  <S.DateCell title={rangeTitle}>
                                    <span>{dateLabel}</span>
                                    {bounds ? (
                                      <AuditDateTimeRangeMenu
                                        rowStartLogId={idBounds.startLogId}
                                        rowEndLogId={idBounds.endLogId}
                                        rangeStartLogId={auditsStartLogId}
                                        rangeEndLogId={auditsEndLogId}
                                        rangeActive={timeRangeControlOn}
                                        disabled={auditsCombineSku}
                                        startLabel={
                                          bounds
                                            ? convertDate(bounds.start).replace('. 00:00am', '').replace('00:00am', '').trim()
                                            : dateLabel
                                        }
                                        endLabel={
                                          bounds
                                            ? convertDate(bounds.end).replace('. 00:00am', '').replace('00:00am', '').trim()
                                            : dateLabel
                                        }
                                        onSetStart={() => applyLogIdToTimeRange('start', idBounds.sourceIds)}
                                        onSetEnd={() => applyLogIdToTimeRange('end', idBounds.sourceIds)}
                                        onClearStart={() => clearLogIdFromTimeRange('start')}
                                        onClearEnd={() => clearLogIdFromTimeRange('end')}
                                      />
                                    ) : null}
                                  </S.DateCell>
                                </S.TD>
                              );
                            }
                            const dateLabel = convertDate(new Date(entry)).replace('. 00:00am', '')
                              .replace('00:00am', '').trim();
                            return (
                              <S.TD
                                key={`LogTD_${h}_${i}`}
                                isLast={isLast}
                                ind={i}
                                highlighted={rowHighlight}
                                isNew={isNew}
                              >
                                <S.DateCell>
                                  <span>{dateLabel}</span>
                                  <AuditDateTimeRangeMenu
                                    rowStartLogId={String(log.id || '')}
                                    rowEndLogId={String(log.id || '')}
                                    rangeStartLogId={auditsStartLogId}
                                    rangeEndLogId={auditsEndLogId}
                                    rangeActive={timeRangeControlOn}
                                    disabled={auditsCombineSku}
                                    startLabel={dateLabel}
                                    endLabel={dateLabel}
                                    onSetStart={() => applyLogIdToTimeRange('start', [String(log.id || '')])}
                                    onSetEnd={() => applyLogIdToTimeRange('end', [String(log.id || '')])}
                                    onClearStart={() => clearLogIdFromTimeRange('start')}
                                    onClearEnd={() => clearLogIdFromTimeRange('end')}
                                  />
                                </S.DateCell>
                              </S.TD>
                            );
                          }
                          if (h === 'message' && isCombinedSku) {
                            const sourceAuditTypes = log.sourceMessages?.length
                              ? log.sourceMessages
                              : [String(log.type || '')];
                            const detailsText = formatCombinedSkuDetailsPreview(
                              sourceAuditTypes,
                              log.combinedLogCount || sourceAuditTypes.length,
                              log.uniqueSourceCount,
                            );
                            return (
                              <S.TD1
                                key={`LogTD_${h}_${i}`}
                                isLast={isLast}
                                ind={i}
                                highlighted={rowHighlight}
                                isNew={isNew}
                              >
                                <S.CombinedSkuDetails title={detailsText}>
                                  <CombinedSkuDetailsText text={detailsText} />
                                </S.CombinedSkuDetails>
                              </S.TD1>
                            );
                          }
                          if (h === 'value' && isUnitsMode) {
                            const units = getLogUnits(log);
                            entry = units == null ? 'N/A' : String(units);
                          }

                          entry = formattedEntry(entry, {
                            unitsMode: h === 'value' && isUnitsMode,
                            parentLog: h === 'message' ? log : undefined,
                          });

                          if (h === 'value') {
                            if (!isCashGroup) {
                              return null;
                            }
                            return (
                              <>
                                <S.TD2
                                  key={`LogTD_${h}_${i}`}
                                  isLast={isLast}
                                  id={`valueTD${i}`}
                                  onMouseLeave={() => onExitValueTD()}
                                  ind={i}
                                  highlighted={rowHighlight}
                                  isNew={isNew}
                                >
                                  {entry[0]}
                                  {
                                    !isUnitsMode && !isCombinedSku && canModifyLogEntry(log) && ((log as any)[h] === '0' || Number((log as any)[h])) && log.id ? (
                                      <S.MoreCont>
                                        <S.RelMoreCont>
                                          <S.MoreIcon0 onClick={() => onClickValueMore(i)}>
                                            <MoreVert fontSize="small" sx={{ marginLeft: 'auto' }} />
                                          </S.MoreIcon0>
                                          <S.MoreIconsCont id={`moreIconsCont${i}`}>
                                            <S.MoreIconsCont1>
                                              <S.MoreIcon
                                                onClick={() => setModal({
                                                  component: (
                                                    <DeleteLog
                                                      onExit={(refreshPg?: boolean) => {
                                                        openOrCloseValueMore(highlightLog);
                                                        setHighlightLog(-1);
                                                        if (refreshPg) setPage(1);
                                                        setIsShowDeleteLog(false);
                                                        setModal(null);
                                                      }}
                                                      id={highlightLog}
                                                      log={displayLogs[highlightLog]}
                                                    />
                                                  ),
                                                  exitOnBgClick: () => {
                                                    setIsShowDeleteLog(false);
                                                    setModal(null);
                                                  }
                                                })}
                                              >
                                                <Bin fontSize="small" />
                                              </S.MoreIcon>
                                              <S.MoreIcon1 onClick={() => onClickEdit(i)}>
                                                <Edit fontSize="small" />
                                              </S.MoreIcon1>
                                            </S.MoreIconsCont1>
                                          </S.MoreIconsCont>
                                          <S.EditAmountCont
                                            id={`editAmountCont${i}`}
                                          >
                                            <S.EditAmountCont1>
                                              <S.EditThisCont>
                                                <S.HeaderPart>
                                                  <S.Header3>Edit amount</S.Header3>
                                                  <S.IconCont2
                                                    onClick={() => {
                                                      setHighlightLog(-1);
                                                      openOrCloseValueMore(i);
                                                    }}
                                                  >
                                                    <Close fontSize="medium" sx={{ scale: '0.65' }} />
                                                  </S.IconCont2>
                                                </S.HeaderPart>
                                                <S.AmountPart>
                                                  {editAmountError ? (
                                                    <S.Info1 isError>{editAmountError}</S.Info1>
                                                  ) : (
                                                    <S.Info1>Enter new amount</S.Info1>
                                                  )}
                                                  <S.InputRow isError={Boolean(editAmountError)}>
                                                    <S.Input1
                                                      value={
                                                        editAmount ? editAmount === '-'
                                                          ? editAmount
                                                          : `${currency}${Number(editAmount).toLocaleString()}`
                                                          : ''
                                                      }
                                                      amount={Number(editAmount)}
                                                      isError={Boolean(editAmountError)}
                                                      onChange={
                                                        (e) => onChangeEditing(e.target.value)
                                                      }
                                                    />
                                                    <S.Button onClick={() => onClickSaveEdit()}>
                                                      Save
                                                    </S.Button>
                                                  </S.InputRow>
                                                </S.AmountPart>
                                              </S.EditThisCont>
                                            </S.EditAmountCont1>
                                          </S.EditAmountCont>
                                        </S.RelMoreCont>
                                      </S.MoreCont>
                                    ) : (null)
                                  }
                                </S.TD2>
                              </>
                            );
                          }
                          if (h === 'message') {
                            const canRestore = canModifyLogEntry(log)
                              && log.type === 'Audit deleted'
                              && Boolean(log.logTable?.id);
                            const canCloseReviewed = !isCombinedSku
                              && isReportContextStandingLogType(log.type)
                              && Boolean(log.id)
                              && canCloseFiledReportStandingLog(
                                String(log.message || ''),
                                permission,
                                staffMaps,
                              );
                            const restoreOpen = restoreOpenId === String(log.id);
                            const closeOpen = closeReportOpenId === String(log.id);
                            return (
                              <S.TD1
                                key={`LogTD_${h}_${i}`}
                                isLast={isLast}
                                ind={i}
                                highlighted={rowHighlight}
                                isNew={isNew}
                              >
                                {
                                  entry.map((e: any, j: number) => <span key={`tdE_${h}_${i}_${j}`}>{e}</span>)
                                }
                                {canRestore ? (
                                  <S.RestoreActions>
                                    <S.RestoreToggle
                                      type="button"
                                      $open={restoreOpen}
                                      title={restoreOpen ? 'Hide restore' : 'Show restore'}
                                      aria-expanded={restoreOpen}
                                      onClick={() => setRestoreOpenId(
                                        restoreOpen ? '' : String(log.id),
                                      )}
                                    >
                                      <FormatListBulleted sx={{ fontSize: 14 }} />
                                    </S.RestoreToggle>
                                    {restoreOpen ? (
                                      <S.RestoreBtn
                                        title="Restore deleted audit"
                                        onClick={() => setModal({
                                          component: (
                                            <RestoreLog
                                              onExit={() => {
                                                setRestoreOpenId('');
                                                setModal(null);
                                              }}
                                              log={log}
                                            />
                                          ),
                                          exitOnBgClick: () => setModal(null),
                                        })}
                                      >
                                        <RestoreFromTrash sx={{ fontSize: 14 }} />
                                        Restore audit
                                      </S.RestoreBtn>
                                    ) : null}
                                  </S.RestoreActions>
                                ) : null}
                                {canCloseReviewed ? (
                                  <S.RestoreActions>
                                    <S.RestoreToggle
                                      type="button"
                                      $open={closeOpen}
                                      title={closeOpen ? 'Hide close' : 'Show close'}
                                      aria-expanded={closeOpen}
                                      onClick={() => setCloseReportOpenId(
                                        closeOpen ? '' : String(log.id),
                                      )}
                                    >
                                      <FormatListBulleted sx={{ fontSize: 14 }} />
                                    </S.RestoreToggle>
                                    {closeOpen ? (
                                      <S.CloseReportBtn
                                        title="Close filed report"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setModal({
                                            component: (
                                              <CloseReviewedReport
                                                onExit={() => {
                                                  setCloseReportOpenId('');
                                                  setModal(null);
                                                }}
                                                auditId={String(log.id)}
                                              />
                                            ),
                                            exitOnBgClick: () => setModal(null),
                                          });
                                        }}
                                      >
                                        <HighlightOff sx={{ fontSize: 14 }} />
                                        Close report
                                      </S.CloseReportBtn>
                                    ) : null}
                                  </S.RestoreActions>
                                ) : null}
                              </S.TD1>
                            );
                          }
                          return (
                            <S.TD
                              key={`LogTD_${h}_${i}`}
                              fontWeight={h === 'type' ? 500 : undefined}
                              isLast={isLast}
                              ind={i}
                              highlighted={rowHighlight}
                              isNew={isNew}
                            >
                              {
                                entry.map((e: any, j: number) => <span key={`tdE_${h}_${i}_${j}`}>{e}</span>)
                              }
                            </S.TD>
                          );
                        })
                      }
                    </S.TR2>
                  );
                })
              }
            </tbody>
          </S.Table>
        </S.Cont1>
        <S.TFoot isCashGroup={isShowTotal}>
          {
            isShowTotal && (
              <>
                <S.TotalFooter>
                  <S.FormatLogs1>
                    <FormatLogs />
                  </S.FormatLogs1>
                  <S.TotalCont>
                    <S.TotalText>Total</S.TotalText>
                    <S.TotalCont1>
                      <S.Total
                        isNegative={!isUnitsMode && (total || 0) < 0}
                        $neutral={isUnitsMode}
                      >
                        {isUnitsMode
                          ? (total || 0).toLocaleString()
                          : `${currency}${(total || 0).toLocaleString()}`}
                      </S.Total>
                    </S.TotalCont1>
                  </S.TotalCont>
                </S.TotalFooter>
                <S.CashFooter>
                  <S.FormatLogs2>
                    <FormatLogs />
                  </S.FormatLogs2>
                </S.CashFooter>
              </>
            )
          }
          <S.PagesFooter isCashGroup={isShowTotal}>
            {
              !isShowTotal && (
                <S.FormatLogs3 $hidden={isReportLogsFocus}>
                  <S.TimeRangeSet
                    type="button"
                    $on={timeRangeControlOn && !auditsCombineSku}
                    disabled={auditsCombineSku}
                    title={auditsCombineSku ? COMBINE_SKU_DATE_ICON_INFO : undefined}
                    onClick={() => {
                      if (auditsCombineSku) return;
                      openTimeRangeModal();
                    }}
                  >
                    <S.All>
                      Time
                      <MediaQuery minWidth={288}>
                        {' '}
                        range
                      </MediaQuery>
                    </S.All>
                  </S.TimeRangeSet>
                </S.FormatLogs3>
              )
            }
            <S.PaginationCont>
              <ReactPaginate
                previousLabel={<ArrowLeft fontSize="small" sx={{ height: '22px', width: '22px' }} />}
                nextLabel={<ArrowRight fontSize="small" sx={{ height: '22px', width: '22px' }} />}
                pageClassName="page-item"
                pageLinkClassName="page-item-link"
                previousClassName="page-arrow"
                previousLinkClassName="page-arrow-link"
                nextClassName="page-arrow"
                nextLinkClassName="page-arrow-link"
                breakLabel={<More fontSize="small" sx={{ height: '22px', width: '22px' }} />}
                breakClassName="page-arrow"
                breakLinkClassName="page-arrow-link"
                pageCount={pagesCount}
                pageRangeDisplayed={2}
                marginPagesDisplayed={2}
                onPageChange={(e) => {
                  setPage(e.selected + 1);
                }}
                containerClassName="pagination"
                activeClassName="active"
                forcePage={page - 1}
              />
            </S.PaginationCont>
          </S.PagesFooter>
        </S.TFoot>
      </S.AllCont>
      {
        !displayLogs.length ? (
          <S.InfoContainer>
            <S.InfoCont>
              <S.EmptyIconWrapper>
                {hasActiveSearch ? (
                  <SearchOff sx={{ fontSize: 64, opacity: 0.25 }} />
                ) : (
                  <ReceiptLong sx={{ fontSize: 64, opacity: 0.25 }} />
                )}
              </S.EmptyIconWrapper>
              <S.NoResults>
                {!logs?.length ? 'No Activity'
                  : hasActiveSearch ? 'No search results'
                    : 'No Results'}
              </S.NoResults>
              <S.Info>
                {!logs?.length
                  ? 'Activity will appear here once it is recorded'
                  : hasActiveSearch
                    ? 'No audits found for your search criteria'
                    : !checked.length
                      ? 'Please select an audit type to view'
                      : boundSelectionHint
                        ? boundSelectionHint
                        : (checked.length && isUseMetricTime)
                          ? 'No audits found for the time range'
                          : (checked.length && allItems && !isUseMetricTime && canUseAuditTools(permission))
                            ? 'No audits found for your selection'
                            : (allItems && !canUseAuditTools(permission))
                              ? 'No audits by you'
                              : (checked.length && !allItems && !isUseMetricTime && isUnitsMode)
                                ? 'No unit logs found for your selection'
                                : (checked.length && !allItems && !isUseMetricTime && cashIn && cashOut)
                                  ? 'No cash flow found for your selection'
                                  : (checked.length && !allItems && !isUseMetricTime && cashIn
                                    && !cashOut)
                                    ? 'No cash-inflow found for your selection'
                                    : (checked.length && !allItems && !isUseMetricTime && !cashIn
                                      && cashOut)
                                      ? 'No cash-outflow found for your selection'
                                      : ''}
              </S.Info>
            </S.InfoCont>
          </S.InfoContainer>
        ) : (null)
      }
    </S.Container>
  );
};

export default LogTable;
